import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Line,
  LineChart,
} from "recharts";
import {
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Download,
  Activity,
  Crown,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";

function StatCard({ label, value, suffix, icon: Icon, tone = "slate" }) {
  const toneBg = {
    slate: "bg-slate-900",
    emerald: "bg-emerald-600",
    amber: "bg-amber-500",
    red: "bg-red-500",
    sky: "bg-sky-600",
  }[tone];
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </div>
        <div className={`h-8 w-8 rounded-md text-white flex items-center justify-center ${toneBg}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">
        {value}
        {suffix && <span className="text-base text-slate-500 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [extended, setExtended] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, e] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/analytics/extended"),
      ]);
      setSummary(s.data);
      setExtended(e.data);
    })();
  }, []);

  if (!summary || !extended) {
    return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  }

  const c = summary.counts;
  const pending = (c.sent || 0) + (c.viewed || 0) + (c.partially_signed || 0);

  const statusData = [
    { name: "Draft", value: c.draft || 0 },
    { name: "Sent", value: c.sent || 0 },
    { name: "Viewed", value: c.viewed || 0 },
    { name: "In prog.", value: c.partially_signed || 0 },
    { name: "Completed", value: c.completed || 0 },
    { name: "Declined", value: c.declined || 0 },
    { name: "Expired", value: c.expired || 0 },
  ];

  const exportCsv = () => {
    window.open(`${API_BASE}/analytics/export.csv`, "_blank");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
            Analytics
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Workspace insights</h1>
          <p className="mt-1 text-sm text-slate-500">
            Throughput, completion velocity, and bottlenecks.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={summary.total} icon={TrendingUp} />
        <StatCard label="Pending" value={pending} icon={Clock} tone="amber" />
        <StatCard label="Completed" value={c.completed || 0} icon={CheckCircle2} tone="emerald" />
        <StatCard label="Declined" value={c.declined || 0} icon={XCircle} tone="red" />
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          label="Avg completion"
          value={extended.avg_completion_hours ?? "—"}
          suffix={extended.avg_completion_hours != null ? "hr" : ""}
          icon={Activity}
          tone="sky"
        />
        <StatCard label="Decline rate" value={extended.decline_rate} suffix="%" icon={XCircle} tone="red" />
        <StatCard label="Expiry rate" value={extended.expiry_rate} suffix="%" icon={Clock} tone="amber" />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <h3 className="text-base font-semibold">Documents by status</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <h3 className="text-base font-semibold">Activity (last 14 days)</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <LineChart data={summary.timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="sent" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top templates + bottlenecks */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="h-4 w-4 text-emerald-600" />
            <h3 className="text-base font-semibold">Top templates</h3>
          </div>
          {extended.top_templates.length === 0 ? (
            <div className="text-xs text-slate-500 py-3">No templates used yet.</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {extended.top_templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    {t.category && (
                      <div className="text-[10px] text-slate-500">{t.category}</div>
                    )}
                  </div>
                  <div className="text-xs font-mono text-slate-500">
                    {t.usage_count || 0}× used
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-amber-600" />
            <h3 className="text-base font-semibold">Pending bottlenecks</h3>
          </div>
          {extended.bottlenecks.length === 0 ? (
            <div className="text-xs text-slate-500 py-3">No pending bottlenecks.</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {extended.bottlenecks.map((b) => (
                <div
                  key={b.email}
                  className="flex items-center justify-between py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 truncate">{b.email}</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs font-medium border border-amber-200 dark:border-amber-800">
                    {b.pending} pending
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
